import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
    EnvelopeSimple,
    PaperPlaneTilt,
    CheckCircle,
    Buildings,
    Briefcase,
    User as UserIcon,
} from "@phosphor-icons/react";

const ROLES = [
    { value: "seeker", label: "I'm a job seeker", icon: Briefcase },
    { value: "company", label: "I'm hiring", icon: Buildings },
    { value: "other", label: "Something else", icon: UserIcon },
];

const initialForm = {
    name: "",
    email: "",
    role: "seeker",
    company: "",
    subject: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null);

    const set = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Valid email required";
        if (!form.subject.trim()) e.subject = "Required";
        if (form.message.trim().length < 10) e.message = "At least 10 characters";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/contact", {
                name: form.name.trim(),
                email: form.email.trim(),
                subject: form.subject.trim(),
                message: form.message.trim(),
                role: form.role,
                company: form.company.trim() || null,
            });
            setSubmitted(data);
            toast.success("Message sent. We'll get back to you soon.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div data-testid="contact-page">
                <Navbar />
                <section className="max-w-[800px] mx-auto px-6 py-24 text-center">
                    <div className="inline-flex w-14 h-14 bg-blue-50 text-blue-600 items-center justify-center border border-blue-200">
                        <CheckCircle size={28} weight="fill" />
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
                        Message received.
                    </h1>
                    <p className="text-slate-600 mt-4 max-w-md mx-auto">
                        Thanks {form.name.split(" ")[0]} — we've routed your note to{" "}
                        <span className="font-mono-plex">founder@jobsiq.tech</span>. Expect a
                        reply within one business day.
                    </p>
                    <div className="mt-6 font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                        Ref · {submitted.submission_id}
                    </div>
                    <div className="mt-10 flex justify-center gap-3 flex-wrap">
                        <Link
                            to="/"
                            data-testid="contact-back-home"
                            className="px-5 py-2.5 bg-slate-950 text-white hover:bg-blue-600"
                        >
                            Back to Jobs IQ
                        </Link>
                        <button
                            data-testid="contact-send-another"
                            onClick={() => {
                                setSubmitted(null);
                                setForm(initialForm);
                            }}
                            className="px-5 py-2.5 border border-slate-300 hover:bg-slate-950 hover:text-white"
                        >
                            Send another message
                        </button>
                    </div>
                </section>
                <Footer />
            </div>
        );
    }

    return (
        <div data-testid="contact-page">
            <Navbar />

            <section className="bg-slate-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-30" />
                <div className="relative max-w-[1100px] mx-auto px-6 py-16">
                    <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-400 inline-flex items-center gap-1.5">
                        <EnvelopeSimple size={14} weight="fill" /> Contact
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl mt-3 leading-[0.95]">
                        Talk to a human.
                    </h1>
                    <p className="mt-6 text-slate-300 max-w-2xl">
                        Questions, partnerships, press, bug reports — anything. Every message lands
                        in <span className="font-mono-plex text-white">founder@jobsiq.tech</span> and gets a real reply.
                    </p>
                </div>
            </section>

            <section className="max-w-[1100px] mx-auto px-6 py-16 grid md:grid-cols-[1fr_320px] gap-10">
                <form
                    onSubmit={submit}
                    className="space-y-5"
                    data-testid="contact-form"
                    noValidate
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                                Name <span className="text-blue-600">*</span>
                            </label>
                            <input
                                data-testid="contact-name"
                                required
                                value={form.name}
                                onChange={set("name")}
                                className={`w-full px-3 py-3 border focus:border-blue-600 focus:outline-none ${
                                    errors.name ? "border-red-500" : "border-slate-300"
                                }`}
                            />
                            {errors.name && (
                                <div className="text-xs text-red-600 mt-1" data-testid="contact-name-error">
                                    {errors.name}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                                Email <span className="text-blue-600">*</span>
                            </label>
                            <input
                                data-testid="contact-email"
                                type="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                className={`w-full px-3 py-3 border focus:border-blue-600 focus:outline-none ${
                                    errors.email ? "border-red-500" : "border-slate-300"
                                }`}
                            />
                            {errors.email && (
                                <div className="text-xs text-red-600 mt-1" data-testid="contact-email-error">
                                    {errors.email}
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-2">
                            I am a <span className="text-blue-600">*</span>
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {ROLES.map((r) => (
                                <button
                                    key={r.value}
                                    type="button"
                                    data-testid={`contact-role-${r.value}`}
                                    onClick={() => setForm((f) => ({ ...f, role: r.value }))}
                                    className={`p-3 border text-left text-sm ${
                                        form.role === r.value
                                            ? "border-slate-950 bg-slate-950 text-white"
                                            : "border-slate-300 hover:border-slate-950"
                                    }`}
                                >
                                    <r.icon size={16} weight="bold" />
                                    <div className="mt-1">{r.label}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {form.role === "company" && (
                        <div>
                            <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                                Company name
                            </label>
                            <input
                                data-testid="contact-company"
                                value={form.company}
                                onChange={set("company")}
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                            Subject <span className="text-blue-600">*</span>
                        </label>
                        <input
                            data-testid="contact-subject"
                            required
                            value={form.subject}
                            onChange={set("subject")}
                            placeholder="What's this about?"
                            className={`w-full px-3 py-3 border focus:border-blue-600 focus:outline-none ${
                                errors.subject ? "border-red-500" : "border-slate-300"
                            }`}
                        />
                        {errors.subject && (
                            <div className="text-xs text-red-600 mt-1" data-testid="contact-subject-error">
                                {errors.subject}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                            Message <span className="text-blue-600">*</span>
                        </label>
                        <textarea
                            data-testid="contact-message"
                            required
                            rows={7}
                            value={form.message}
                            onChange={set("message")}
                            placeholder="Tell us what you need. Be specific — we read every word."
                            className={`w-full px-3 py-3 border focus:border-blue-600 focus:outline-none ${
                                errors.message ? "border-red-500" : "border-slate-300"
                            }`}
                        />
                        <div className="flex items-center justify-between mt-1">
                            {errors.message ? (
                                <div className="text-xs text-red-600" data-testid="contact-message-error">
                                    {errors.message}
                                </div>
                            ) : (
                                <div className="text-xs text-slate-500">Minimum 10 characters.</div>
                            )}
                            <div className="text-[10px] font-mono-plex text-slate-400">
                                {form.message.length} chars
                            </div>
                        </div>
                    </div>

                    <div className="pt-2 flex items-center justify-between flex-wrap gap-3">
                        <p className="text-xs text-slate-500 max-w-sm">
                            By sending you agree to our{" "}
                            <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>{" "}
                            and{" "}
                            <Link to="/terms" className="text-blue-600 hover:underline">Terms</Link>.
                        </p>
                        <button
                            data-testid="contact-submit-btn"
                            disabled={submitting}
                            className="px-6 py-3 bg-blue-600 text-white hover:bg-slate-950 disabled:opacity-60 inline-flex items-center gap-2"
                        >
                            <PaperPlaneTilt size={16} weight="fill" />
                            {submitting ? "Sending…" : "Send message"}
                        </button>
                    </div>
                </form>

                <aside className="space-y-4">
                    <div className="border border-slate-200 p-5">
                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                            Direct line
                        </div>
                        <a
                            href="mailto:founder@jobsiq.tech"
                            className="font-display text-lg text-blue-600 hover:underline inline-flex items-center gap-2"
                            data-testid="contact-mailto-link"
                        >
                            <EnvelopeSimple size={18} /> founder@jobsiq.tech
                        </a>
                        <p className="text-xs text-slate-500 mt-2">
                            For partnerships, press and security disclosures.
                        </p>
                    </div>
                </aside>
            </section>

            <Footer />
        </div>
    );
}
